import React from "react";
import { createUseStyles } from "react-jss";
import PropTypes from "prop-types";
import _ from "lodash";
import jssVariables from "../../../../jss_setup";
import {
  StyleTypeContext,
  TranslationContext,
  countries,
} from "../../../../umbilical_tools";
import { useContext } from "react";
import { useSelector } from "react-redux";
import { DateTime } from "luxon";
import {
  DatepickerField,
  SelectField,
} from "../../../../components/forms/inputGroup/helpers";
import { InputField } from "../../../../components/forms/inputGroup";
import { Form, useFormikContext } from "formik";

const fieldCustomStyles = {
  label: {
    fontSize: "large",
  },
};

function requiredFields({ required_fields, t, idx, guest_type }) {
  return (
    <React.Fragment>
      {required_fields.includes("place_of_birth") && (
        <InputField
          as="input"
          label={t("placeOfBirth")}
          name={`${guest_type}.${idx}.place_of_birth`}
          customStyles={fieldCustomStyles}
          placeholder="----"
        />
      )}
      {required_fields.includes("passport_number") && (
        <InputField
          as="input"
          label={t("passportNumber")}
          name={`${guest_type}.${idx}.passport_number`}
          customStyles={fieldCustomStyles}
          placeholder="----"
        />
      )}
      {required_fields.includes("passport_place_of_issue") && (
        <InputField
          as="input"
          label={t("passportPlaceOfIssue")}
          name={`${guest_type}.${idx}.passport_place_of_issue`}
          customStyles={fieldCustomStyles}
          placeholder="----"
        />
      )}
      {required_fields.includes("passport_start_date") && (
        <DatepickerField
          label={t("passportStartDate")}
          name={`${guest_type}.${idx}.passport_start_date`}
          dateFormat="MMM d, yyyy"
          withPortal={true}
          maxDate={DateTime.now().toString()}
          customStyles={fieldCustomStyles}
          placeholder="----"
          showYearDropdown={true}
          yearDropdownItemsNum={90}
        />
      )}
      {required_fields.includes("passport_expiry_date") && (
        <DatepickerField
          label={t("passportExpiryDate")}
          name={`${guest_type}.${idx}.passport_expiry_date`}
          dateFormat="MMM d, yyyy"
          withPortal={true}
          minDate={DateTime.now().toString()}
          customStyles={fieldCustomStyles}
          placeholder="----"
          showYearDropdown={true}
          yearDropdownItemsNum={90}
        />
      )}
      {required_fields.includes("city_code") && (
        <InputField
          as="input"
          label={t("cityCode")}
          name={`${guest_type}.${idx}.city_code`}
          customStyles={fieldCustomStyles}
          placeholder="----"
        />
      )}
    </React.Fragment>
  );
}
const formStyles = createUseStyles({
  TravellerForm: (props) => ({
    display: "grid",
    maxHeight: "60vh",
    overflowY: "auto",
    ..._.get(props, "customStyles.TravellersForms", {}),
  }),
  form: (props) => ({
    display: "grid",
    gridTemplateColumns: "1fr",
    gridGap: `calc(${jssVariables.normal_gap} * 2)`,
    justifyContent: "space-between",
    padding: jssVariables.normal_gap,
    ..._.get(props, "customStyles.form", {}),
  }),
  passengerTitle: (props) => ({
    fontSize: "large",
    fontWeight: "700",
    ..._.get(props, "customStyles.passengerTitle", {}),
  }),
  divider: (props) => ({
    border: `0.5px solid ${jssVariables.colors.border}`,
    width: "100%",
    ..._.get(props, "customStyles.divider", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    TravellerForm: (props) => ({
      ..._.get(props, "customStyles.TravellersForms.smallscreen", {}),
    }),
    form: (props) => ({
      gridTemplateColumns: "repeat(3,1fr)",
      ..._.get(props, "customStyles.form.smallscreen", {}),
    }),
    passengerTitle: (props) => ({
      gridColumn: "span 3",
      fontWeight: "600",
      ..._.get(props, "customStyles.passengerTitle.smallscreen", {}),
    }),
    divider: (props) => ({
      gridColumn: "span 3",
      ..._.get(props, "customStyles.divider.smallscreen", {}),
    }),
  },
  [`@media ${jssVariables.media.bigscreen}`]: {
    TravellerForm: (props) => ({
      ..._.get(props, "customStyles.TravellersForms.bigscreen", {}),
    }),
    passengerTitle: (props) => ({
      ..._.get(props, "customStyles.passengerTitle.bigscreen", {}),
    }),
    divider: (props) => ({
      ..._.get(props, "customStyles.divider.bigscreen", {}),
    }),
  },
});
export const TravellerForm = ({ required_fields }) => {
  const { type, brand, cStyler } = useContext(StyleTypeContext);
  const { t } = useContext(TranslationContext);
  const { values } = useFormikContext();

  const classes = formStyles({
    customStyles: cStyler({ type, brand, objName: "TravellerForm" }),
  });

  return (
    <div className={classes.TravellerForm}>
      <Form className={classes.form}>
        {values.adults.map((x, idx) => (
          <React.Fragment key={idx}>
            <div className={classes.passengerTitle}>
              {t("passenger")} {idx + 1}: {t("adult")}{" "}
              {idx === 0 && `(${t("groupLeader")})`}
            </div>
            <SelectField
              id={`title-${idx}`}
              label={t("title")}
              name={`adults.${idx}.title`}
              options={[
                ["DR", "Dr."],
                ["MR", "Mr."],
                ["MRS", "Mrs."],
                ["MS", "Ms."],
              ]}
              customStyles={fieldCustomStyles}
              placeholder="----"
            />
            <InputField
              id={`first_name-${idx}`}
              as="input"
              label={t("firstName")}
              name={`adults.${idx}.first_name`}
              customStyles={fieldCustomStyles}
              placeholder="----"
            />
            <InputField
              id={`last_name-${idx}`}
              as="input"
              label={t("lastName")}
              name={`adults.${idx}.last_name`}
              customStyles={fieldCustomStyles}
              placeholder="----"
            />
            <DatepickerField
              id={`birthday-${idx}`}
              label={t("dateOfBirth")}
              name={`adults.${idx}.birthday`}
              dateFormat="MMM d, yyyy"
              withPortal={true}
              minDate={DateTime.now().minus({ years: 59 }).toString()}
              maxDate={DateTime.now().minus({ years: 18 }).toString()}
              customStyles={fieldCustomStyles}
              placeholder="----"
              showYearDropdown={true}
              yearDropdownItemsNum={90}
            />
            <SelectField
              id={`nationality-${idx}`}
              label={t("nationality")}
              name={`adults.${idx}.nationality`}
              options={_.sortBy(
                Object.entries(countries).map(([code, name]) => [
                  code,
                  `${name} (${code})`,
                ]),
                (v) => v[1]
              )}
              customStyles={fieldCustomStyles}
              placeholder="----"
            />
            {(idx === 0 || required_fields.includes("telephone")) && (
              <InputField
                id={`phone-${idx}`}
                as="input"
                label={t("phone")}
                name={`adults.${idx}.telephone`}
                customStyles={fieldCustomStyles}
                placeholder="----"
              />
            )}
            {(idx === 0 || required_fields.includes("email")) && (
              <InputField
                id={`email-${idx}`}
                as="input"
                label={t("email")}
                name={`adults.${idx}.email`}
                customStyles={fieldCustomStyles}
                placeholder="----"
              />
            )}
            {requiredFields({
              required_fields,
              t,
              idx,
              guest_type: "adults",
            })}
            {idx + 1 < values.adults.length && (
              <hr className={classes.divider} />
            )}
          </React.Fragment>
        ))}
        {values.seniors.length > 0 && values.adults.length > 0 && (
          <hr className={classes.divider} />
        )}
        {values.seniors.map((s, idx) => (
          <React.Fragment key={idx}>
            <div className={classes.passengerTitle}>
              {t("passenger")} {values.adults.length + idx + 1}: {t("senior")}{" "}
              {idx === 0 &&
                values.adults.length === 0 &&
                `(${t("groupLeader")})`}
            </div>
            <SelectField
              label={t("title")}
              name={`seniors.${idx}.title`}
              options={[
                ["DR", "Dr."],
                ["MR", "Mr."],
                ["MRS", "Mrs."],
                ["MS", "Ms."],
              ]}
              customStyles={fieldCustomStyles}
              placeholder="----"
            />
            <InputField
              as="input"
              label={t("firstName")}
              name={`seniors.${idx}.first_name`}
              customStyles={fieldCustomStyles}
              placeholder="----"
            />
            <InputField
              as="input"
              label={t("lastName")}
              name={`seniors.${idx}.last_name`}
              customStyles={fieldCustomStyles}
              placeholder="----"
            />
            <DatepickerField
              label={t("dateOfBirth")}
              name={`seniors.${idx}.birthday`}
              dateFormat="MMM d, yyyy"
              withPortal={true}
              maxDate={DateTime.now().minus({ years: 60 }).toString()}
              customStyles={fieldCustomStyles}
              placeholder="----"
              showYearDropdown={true}
              yearDropdownItemsNum={90}
            />
            <SelectField
              label={t("nationality")}
              name={`seniors.${idx}.nationality`}
              options={_.sortBy(
                Object.entries(countries).map(([code, name]) => [
                  code,
                  `${name} (${code})`,
                ]),
                (v) => v[1]
              )}
              customStyles={fieldCustomStyles}
              placeholder="----"
            />
            {((idx === 0 && values.adults.length === 0) ||
              required_fields.includes("telephone")) && (
              <InputField
                as="input"
                label={t("phone")}
                name={`seniors.${idx}.phone`}
                customStyles={fieldCustomStyles}
                placeholder="----"
              />
            )}
            {((idx === 0 && values.adults.length === 0) ||
              required_fields.includes("email")) && (
              <InputField
                as="input"
                label={t("email")}
                name={`seniors.${idx}.email`}
                customStyles={fieldCustomStyles}
                placeholder="----"
              />
            )}
            {requiredFields({
              required_fields,
              t,
              idx,
              guest_type: "seniors",
            })}
            {idx + 1 < values.seniors.length && (
              <hr className={classes.divider} />
            )}
          </React.Fragment>
        ))}
        {values.children.length > 0 &&
          (values.adults.length > 0 || values.seniors.length > 0) && (
            <hr className={classes.divider} />
          )}
        {values.children.map((c, idx) => (
          <React.Fragment key={idx}>
            <div className={classes.passengerTitle}>
              {t("passenger")}{" "}
              {values.adults.length + values.seniors.length + idx + 1}:{" "}
              {t("child")}{" "}
            </div>
            <InputField
              as="input"
              label={t("firstName")}
              name={`children.${idx}.first_name`}
              customStyles={fieldCustomStyles}
              placeholder="----"
            />
            <InputField
              as="input"
              label={t("lastName")}
              name={`children.${idx}.last_name`}
              customStyles={fieldCustomStyles}
              placeholder="----"
            />
            <DatepickerField
              label={t("dateOfBirth")}
              name={`children.${idx}.birthday`}
              dateFormat="MMM d, yyyy"
              withPortal={true}
              minDate={DateTime.now().minus({ years: 17 }).toString()}
              maxDate={DateTime.now().toString()}
              customStyles={fieldCustomStyles}
              placeholder="----"
              showYearDropdown={true}
              yearDropdownItemsNum={90}
            />
            <SelectField
              label={t("nationality")}
              name={`children.${idx}.nationality`}
              options={_.sortBy(
                Object.entries(countries).map(([code, name]) => [
                  code,
                  `${name} (${code})`,
                ]),
                (v) => v[1]
              )}
              customStyles={fieldCustomStyles}
              placeholder="----"
            />
            {requiredFields({
              required_fields,
              t,
              idx,
              guest_type: "children",
            })}
            {idx + 1 < values.children.length && (
              <hr className={classes.divider} />
            )}
          </React.Fragment>
        ))}
      </Form>
    </div>
  );
};
TravellerForm.defaultProps = {
  required_fields: [],
};
TravellerForm.propTypes = {
  required_fields: PropTypes.array.isRequired,
};

const styles = createUseStyles({
  TravelInfo: (props) => ({
    display: "grid",
    gridGap: jssVariables.normal_gap,
    boxShadow: jssVariables.shadows.normalShadow,
    borderRadius: jssVariables.half_gap,
    padding: jssVariables.normal_gap,
    ..._.get(props, "customStyles.TravelInfo", {}),
  }),
  header: (props) => ({
    display: "grid",
    alignItems: "start",
    fontSize: "x-large",
    background: jssVariables.colors.base_color,
    borderRadius: jssVariables.half_gap,
    padding: [jssVariables.half_gap, jssVariables.normal_gap],
    color: "white",
    fontWeight: "500",
    ..._.get(props, "customStyles.header", {}),
  }),
  content: (props) => ({
    display: "grid",
    gridGap: jssVariables.normal_gap,
    ..._.get(props, "customStyles.content", {}),
  }),
  passenger: (props) => ({
    display: "grid",
    gridTemplateColumns: "1fr",
    ..._.get(props, "customStyles.passenger", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    TravelInfo: (props) => ({
      ..._.get(props, "customStyles.TravelInfo.smallscreen", {}),
    }),
    header: (props) => ({
      padding: `calc(${jssVariables.normal_gap} * 1.5) ${jssVariables.double_gap}`,

      ..._.get(props, "customStyles.header.smallscreen", {}),
    }),
    content: (props) => ({
      ..._.get(props, "customStyles.content.smallscreen", {}),
    }),
  },
  [`@media ${jssVariables.media.bigscreen}`]: {
    TravelInfo: (props) => ({
      ..._.get(props, "customStyles.TravelInfo.bigscreen", {}),
    }),
    header: (props) => ({
      ..._.get(props, "customStyles.header.bigscreen", {}),
    }),
    content: (props) => ({
      ..._.get(props, "customStyles.content.bigscreen", {}),
    }),
  },
});
const TravellersForms = ({ required_fields }) => {
  const { t } = useContext(TranslationContext);
  const { type, brand, cStyler } = useContext(StyleTypeContext);

  const classes = styles({
    customStyles: cStyler({
      type,
      brand,
      objName: "TravelInfo",
    }),
  });

  return (
    <div className={classes.TravelInfo}>
      <div className={classes.header}>
        {t("passenger")} {t("details")}
      </div>
      <div className={classes.content}>
        <TravellerForm required_fields={required_fields} />
      </div>
    </div>
  );
};
TravellersForms.defaultProps = {
  required_fields: [],
};
TravellersForms.propTypes = {
  required_fields: PropTypes.array.isRequired,
};
export default TravellersForms;
